// Firebase client SDK initialization. The exported handles are the only Firebase
// accessors the app should use — do NOT call initializeApp() anywhere else.
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics, isSupported, logEvent, type Analytics } from 'firebase/analytics';

const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

for (const [k, v] of Object.entries(cfg)) {
  if (!v) throw new Error(`Firebase config missing: ${k}. Check .env.local.`);
}

export const firebaseApp = initializeApp(cfg);
export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const functions = getFunctions(firebaseApp, 'us-central1');

if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

// Firebase Analytics (GA4). Wired in v0.18.2 to give us an INDEPENDENT
// daily-active-user signal we own. Play Console's DAU is Google-measured at
// the OS level and never reflected any app-side telemetry (the app had none
// before this). This lets us cross-check: if GA4 shows active users while
// Play Console shows zero, the gap is a Play measurement/attribution artifact;
// if GA4 also shows zero, engagement genuinely collapsed.
//
// Merely initializing analytics fires GA4's automatic session_start /
// first_open, which is what populates DAU — so the track('app_open') call on
// App mount is what bootstraps the signal. Initialized lazily and guarded by
// isSupported() so it never breaks unsupported browsers or the local emulator
// runtime; track() no-ops (never throws) if analytics can't initialize.
let analyticsReady: Promise<Analytics | null> | null = null;

function getAnalyticsInstance(): Promise<Analytics | null> {
  if (analyticsReady) return analyticsReady;
  // Never emit measurement traffic from local dev (emulator) runs into the
  // production GA4 property.
  if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
    analyticsReady = Promise.resolve(null);
    return analyticsReady;
  }
  analyticsReady = isSupported()
    .then((ok) => (ok ? getAnalytics(firebaseApp) : null))
    .catch(() => null);
  return analyticsReady;
}

/**
 * Fire a GA4 event. Safe to call from anywhere — initializes analytics on
 * first use, no-ops if unsupported / emulator / init fails, never throws.
 */
export function track(eventName: string, params?: Record<string, unknown>): void {
  void getAnalyticsInstance().then((a) => {
    if (a) logEvent(a, eventName, params);
  });
}
